import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { PORTAL_KEYS, listarVagasPublicas, type PublicJob } from '../api';

const WORK_MODEL_LABELS: Record<string, string> = {
  presencial: 'Presencial',
  hibrido: 'Híbrido',
  remoto: 'Remoto',
};

/**
 * Catálogo público de vagas.
 *
 * Fora do layout autenticado: sem sidebar, sem header do app e sem
 * token. Só vagas publicadas com prazo vigente chegam aqui, e o filtro é
 * do servidor.
 */
export default function VagasPublicasPage(): React.ReactElement {
  const [city, setCity] = useState('');
  const [workModel, setWorkModel] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: PORTAL_KEYS.vagas({ city, workModel }),
    queryFn: () => listarVagasPublicas({ city: city || undefined, workModel: workModel || undefined }),
  });

  const vagas = data?.data ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-6">
          <div>
            <p className="text-2xl font-bold text-blue-600">nexos</p>
            <h1 className="mt-1 text-lg font-semibold text-gray-900">Trabalhe com a gente</h1>
          </div>
          <Link
            to="/vagas/acompanhar"
            className="text-sm font-medium text-blue-600 hover:underline"
          >
            Acompanhar minha candidatura
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <TextInput
              label="Cidade"
              icon={<Search className="h-4 w-4" />}
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
          </div>
          <div className="w-48">
            <AutocompleteSelect
              label="Modalidade"
              options={[
                { value: '', label: 'Todas' },
                { value: 'presencial', label: 'Presencial' },
                { value: 'hibrido', label: 'Híbrido' },
                { value: 'remoto', label: 'Remoto' },
              ]}
              value={workModel}
              onChange={setWorkModel}
            />
          </div>
        </div>

        {isError && (
          <Alert variant="error">
            Não foi possível carregar as vagas agora. Tente novamente em instantes.
          </Alert>
        )}

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : vagas.length === 0 ? (
          <EmptyState
            title="Nenhuma vaga aberta no momento"
            description="Volte em breve — novas oportunidades são publicadas com frequência."
          />
        ) : (
          <ul className="space-y-3">
            {vagas.map((vaga: PublicJob) => (
              <li key={vaga.id}>
                <Link
                  to={`/vagas/${vaga.id}`}
                  className="block rounded-lg border border-gray-200 bg-white p-4 transition hover:border-blue-300 hover:shadow-sm"
                >
                  <h2 className="text-base font-semibold text-gray-900">{vaga.title}</h2>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {vaga.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {vaga.city}
                      </span>
                    )}
                    {vaga.workModel && <span>{WORK_MODEL_LABELS[vaga.workModel]}</span>}
                  </p>
                  <p className="mt-2 line-clamp-2 text-sm text-gray-600">{vaga.description}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
